import React, { useEffect, useState } from "react";
import PageHeader from "../../components/PageHeader";
import { useFormik } from "formik";
import FormInput from "../../forms/FormInput";
import { Grid } from "@mui/material";
import FormDialog from "../../forms/FormDialog";
import CustomTable from "../../components/Tables/CustomTable";
import { damageCodeKeys } from "../../data/TableData/damageCode";
import URLS from "../../services/urlConstants";
import { post, get, del, put } from "../../services/apiMethods";
import CustomInput from "../../components/SecondaryForms/CustomInput";
import CustomWarehouseFilter from "../../components/SecondaryForms/CustomWarehouseFilter";
import Warehouse from "./Warehouse";
import CustomSearch from "../../components/Tables/CustomSearch";

export default function DamageCode() {
  const [isReady, setIsReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [dataToEdit, setDataToEdit] = useState(null);
  const [data, setData] = useState([]);
  const [formFields, setFormFields] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [readOnly, setReadOly] = useState(false);
  const [searchFilter, setSearchFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSerach] = useState(true);

  const getData = async () => {
    setIsReady(false);
    try {
      const data = await get(URLS.damageCode.list, {
        params: {
          search: {
            warehouse: selectedWarehouse,
          },
        },
      });
      setData(data.data.result);
    } catch (error) {
      console.log(error);
    }
    setTimeout(() => {
      setIsReady(true);
    }, 300);
  };

  const getFormDetails = async () => {
    try {
      const { data } = await get(URLS.forms.read, {
        params: { collectionName: "damageCode", warehouse: selectedWarehouse },
      });
      setFormFields(data.result ? data.result.fields : null);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (selectedWarehouse) {
      getData();
      getFormDetails();
    } else {
      setIsReady(true);
    }
  }, [selectedWarehouse]);

  const handleOnFieldAdd = () => {
    getData();
    getFormDetails();
  };

  const handleCreateOrUpdate = async (values) => {
    try {
      const payload = {
        ...values,
        meta: formFields,
      };
      setLoading(true);
      if (dataToEdit) {
        const data = await put(
          URLS.damageCode.update + "/" + dataToEdit._id,
          payload
        );
      } else {
        const data = await post(URLS.damageCode.create, payload);
      }
      getData();
      handleModalClose();
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const formik = useFormik({
    initialValues: {
      code: dataToEdit?.code || "",
      description: dataToEdit?.description || "",
      warehouse: selectedWarehouse || "",
    },
    onSubmit: (values) => handleCreateOrUpdate(values),
    enableReinitialize: true,
  });

  // modal actions
  const openDataAddOrUpdateModal = () => setOpen(true);

  const handleModalClose = () => {
    setOpen(false);
    setTimeout(() => {
      setDataToEdit(null);
      setFormFields(
        formFields?.map((f) => ({
          ...f,
          value: "",
        }))
      );
      formik.resetForm();
    }, 200);
  };

  // edit data actions
  const handleCustomInputChange = (e) => {
    const { name, value } = e.target;
    setFormFields(
      formFields?.map((f) => (f.inputLabel === name ? { ...f, value } : f))
    );
  };

  const handleSetDataToEdit = (data) => {
    setReadOly(false);
    setDataToEdit(data);
    const fields = data?.meta;
    setFormFields(
      formFields?.map((f) => ({
        ...f,
        value:
          fields?.find((fi) => fi.inputLabel === f.inputLabel)?.value || "",
      }))
    );

    setOpen(true);
  };

  const handleShowData = (data) => {
    setReadOly(true);
    setDataToEdit(data);
    const fields = data?.meta;
    setFormFields(
      formFields?.map((f) => ({
        ...f,
        value:
          fields?.find((fi) => fi.inputLabel === f.inputLabel)?.value || "",
      }))
    );
    setOpen(true);
  };

  const handleDelete = async (id, reason) => {
    try {
      const response = await del(URLS.damageCode.delete + "/" + id, {
        params: {
          reasonForDelete: reason,
        },
      });
      const filtered = data.filter((item) => item._id !== id);
      setData(filtered);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <PageHeader
        collection="Damage code"
        onAddClick={openDataAddOrUpdateModal}
        title="Damage code"
        onFieldAdd={handleOnFieldAdd}
        modelName="damageCode"
        module="Damage code"
        addButtonDisabled={!selectedWarehouse}
      />

      {/* == warehouse filter components */}
      <CustomWarehouseFilter
        setSelectedWarehouse={setSelectedWarehouse}
        selectedWarehouse={selectedWarehouse}
        data={data}
        setSearch={setSearch}
        setSearchFilter={setSearchFilter}
        search={search}
        placeholder="Code"
        keys={["code"]}
        showSearch={showSearch}
      />

      <CustomTable
        actions={["edit", "delete", "view"]}
        isDataReady={isReady}
        bodyDataModal="damage code"
        bodyData={search ? searchFilter : data}
        tableKeys={damageCodeKeys}
        onEditClick={handleSetDataToEdit}
        onDeleteClick={handleDelete}
        onViewClick={handleShowData}
        module="Damage code"
        formFields={formFields}
      />

      <FormDialog
        readOnly={readOnly}
        open={open}
        onClose={handleModalClose}
        submitButtonTitle={dataToEdit ? "Update" : "Add"}
        formik={formik}
        formTitle={dataToEdit ? "Update damage code" : "Add damage code"}
        adding={loading}
      >
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              required={true}
              name="code"
              type="text"
              formik={formik}
              label="Damage Code"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              required={true}
              name="description"
              type="text"
              formik={formik}
              label="Description"
            />
          </Grid>
          {formFields
            ? formFields?.map((f, i) => (
                <Grid key={i} item xs={12} sm={6}>
                  <CustomInput
                    onChange={handleCustomInputChange}
                    name={f.inputLabel}
                    value={f.value}
                    required={f.isRequired}
                    label={f.inputLabel}
                    type={f.inputType}
                  />
                </Grid>
              ))
            : null}
        </Grid>
      </FormDialog>
    </>
  );
}
